import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Loader,
} from "lucide-react";
import { api } from "../api/greenclub";

const FinalizeRepresentativeRegistration = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [preCadastro, setPreCadastro] = useState(null);
  const [formData, setFormData] = useState({
    senha: "",
    confirmarSenha: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = searchParams.get("token");

    if (!tokenFromUrl) {
      setError("Token de finalização não encontrado. Verifique o link.");
      setLoading(false);
      return;
    }
    setToken(tokenFromUrl);

    const fetchPreCadastro = async () => {
      try {
        const response = await api.getRepresentativePreRegistrationByToken(
          tokenFromUrl
        );
        if (response.success) {
          setPreCadastro(response.data);
        } else {
          setError(
            response.error || "Token inválido, expirado ou já utilizado."
          );
        }
      } catch (err) {
        setError(
          "Erro ao buscar dados do pré-cadastro. Tente novamente mais tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPreCadastro();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (formData.senha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      setLoading(false);
      return;
    }

    if (formData.senha !== formData.confirmarSenha) {
      setError("As senhas não coincidem.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.finalizeRepresentativePreRegistration({
        token,
        senha: formData.senha,
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || "Não foi possível finalizar o cadastro.");
      }
    } catch (err) {
      setError("Ocorreu um erro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 text-emerald-600 animate-spin" />
        <p className="ml-3 text-gray-700">Verificando convite...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Link Inválido
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => (window.location.href = "/")}
            className="mt-6 w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-12 h-12 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Cadastro Finalizado!
          </h2>
          <p className="text-gray-600">
            Seu cadastro foi concluído com sucesso. Você já pode acessar o
            portal de representantes.
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="mt-6 w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700"
          >
            Ir para o Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Finalize seu Cadastro, {preCadastro?.nome}!
        </h1>
        <p className="text-gray-600 mb-6">
          Você foi convidado para ser um representante GreenClub. Defina sua
          senha para ativar seu acesso.
        </p>

        <div className="space-y-3 text-sm text-gray-700 mb-6 p-4 bg-gray-50 rounded-lg border">
          <p className="flex items-center">
            <Mail className="w-4 h-4 mr-2 text-gray-500" />
            <strong>Email:</strong>&nbsp;{preCadastro?.email}
          </p>
          <p className="flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-gray-500" />
            <strong>Território:</strong>&nbsp;{preCadastro?.territorio}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Crie sua senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.senha}
                onChange={(e) =>
                  setFormData({ ...formData, senha: e.target.value })
                }
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Mínimo 6 caracteres"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirme sua senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmarSenha}
                onChange={(e) =>
                  setFormData({ ...formData, confirmarSenha: e.target.value })
                }
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Repita a senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-60"
          >
            {loading ? "Finalizando..." : "Concluir Cadastro e Acessar"}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

export default FinalizeRepresentativeRegistration;
